// Orchestrator — owns submit handler + API calls + navigation. ~150 lines.
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, ArrowLeft, ArrowRight, ChevronRight } from "lucide-react";
import { useStore } from "../../store";
import { WIZARD_TEMPLATES, PARTY_LAYER_COLOR } from "../../data/wizardTemplates";
import {
  useCreateIncident, uploadAttachments, useCreateServiceRequest,
} from "../../api/sitroom";
import { useWizardState } from "./useWizardState";
import { WizardStepIndicator } from "./WizardStepIndicator";
import { WizardStep1Template } from "./WizardStep1Template";
import { WizardStep2Details } from "./WizardStep2Details";
import { WizardStep3Parties } from "./WizardStep3Parties";
import { WizardStep4Review } from "./WizardStep4Review";
import { STEPS } from "./wizardAtoms";

export default function NewIncidentWizard() {
  const navigate = useNavigate();
  const addToast = useStore((s) => s.addToast);
  const openPanel = useStore((s) => s.openPanel);
  const createIncident = useCreateIncident();
  const createServiceRequest = useCreateServiceRequest();

  const {
    form, setForm, step, goTo, goNext, goBack, completed,
    errors, validateStep, canNextStep1, canNextStep2,
  } = useWizardState();

  const [busy, setBusy] = React.useState(false);
  const [progress, setProgress] = React.useState("");

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (step === 1) navigate("/incidents");
        else goBack();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
     
  }, [step]);

  const doSubmit = async (asDraft) => {
    if (!validateStep(2)) {
      goTo(2);
      return;
    }
    setBusy(true);
    setProgress("Creating incident…");
    try {
      const tpl = WIZARD_TEMPLATES.find((t) => t.value === form.template);
      const linkedParties = [
        ...tpl.parties.map((p) => ({ name: p, role: p, color: PARTY_LAYER_COLOR[p] || "#185FA5", required: true })),
        ...(form.additionalParties || []).map((p) => ({
          name: p.name, role: p.role, color: PARTY_LAYER_COLOR[p.type] || "#9ec3ec", required: false,
        })),
      ];

      const created = await createIncident.mutateAsync({
        title: form.title.trim(),
        template: tpl.label,
        templateKey: tpl.value,
        description: form.description.trim(),
        partNumber: form.parts.map((p) => p.part_number).join(", ") || "—",
        partNumbers: form.parts,
        line: form.line,
        impact: `${form.units || 0} units · ${form.hours || 0}h`,
        units: Number(form.units || 0),
        hours: Number(form.hours || 0),
        loss: form.loss ? `€${form.loss}` : "—",
        lossEur: Number((form.loss || "0").replace(/\D/g, "")),
        severity: form.severity,
        status: asDraft ? "draft" : "open",
        sla: 0,
        age: "0m",
        parties: linkedParties.map((p) => p.name).join(" ↔ "),
        opener: "G. Castro",
        linkedParties,
        linked_tenant_ids: linkedParties.map((p) => p.name),
        internalRef: form.internalRef,
        timeline: [],
        comments: [],
        attachments: [],
        notifyOnSubmit: form.notifyNow && !asDraft,
      });

      if (form.files.length > 0) {
        setProgress(`Uploading ${form.files.length} attachment(s)…`);
        try {
          await uploadAttachments(created.id, form.files);
        } catch (e) {
          addToast({ type: "info", title: "Attachments failed", body: e?.response?.data?.detail || "Could not upload files" });
        }
      }

      if (form.serviceRequests && form.serviceRequests.length > 0) {
        setProgress(`Creating ${form.serviceRequests.length} service request(s)…`);
        const results = await Promise.allSettled(
          form.serviceRequests.map((sr) =>
            createServiceRequest.mutateAsync({
              incidentId: created.id,
              body: {
                type: sr.type,
                title: (sr.title || `${sr.type} request`).trim(),
                description: (sr.description || "").trim() || null,
                priority: sr.priority || "medium",
                due_date: sr.due_date || null,
                assignee_name: sr.assignee_name || null,
                assignee_user_id: sr.assignee_user_id || null,
                assignee_tenant_id: sr.assignee_tenant_id || null,
              },
            }),
          ),
        );
        const failed = results.filter((r) => r.status === "rejected").length;
        if (failed > 0) {
          addToast({ type: "info", title: "Some service requests failed", body: `${failed} of ${results.length} could not be created.` });
        }
      }

      setProgress(asDraft ? "Saved as draft" : "Notifying parties · starting SLA timer…");
      addToast({
        type: "info",
        title: asDraft ? "Draft saved" : "Incident opened",
        body: `${created.id} · ${form.title}`,
        action: { label: "View →", onClick: () => openPanel(created.id) },
      });

      setTimeout(() => {
        navigate(`/incidents?focus=${created.id}`);
      }, 700);
    } catch (e) {
      setBusy(false);
      setProgress("");
      addToast({
        type: "info",
        title: "Submit failed",
        body: e?.response?.data?.detail || "Could not create incident",
      });
    }
  };

  return (
    <div
      className="min-h-screen p-4 sm:p-8"
      style={{ background: "#08090d" }}
      data-testid="new-incident-wizard"
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1
              data-testid="view-title"
              style={{
                fontFamily: "Syne, sans-serif", fontSize: 26, fontWeight: 700,
                color: "#f0f2f8", letterSpacing: "-0.4px",
              }}
            >
              New incident
            </h1>
            <div
              style={{
                fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#9198b0",
                letterSpacing: "1.5px", textTransform: "uppercase", marginTop: 4,
              }}
            >
              Step {step} of 4 · {STEPS[step - 1].label}
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate("/incidents")}
            className="rounded-md p-1.5 transition-colors"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.10)", color: "#9198b0", cursor: "pointer" }}
            aria-label="Close wizard"
            data-testid="wizard-close-btn"
          >
            <X size={16} />
          </button>
        </div>

        <WizardStepIndicator current={step} onStepClick={goTo} completed={completed} />

        <div className="rounded-md p-4 sm:p-6" style={{ background: "#0a0c14", border: "1px solid rgba(255,255,255,0.06)" }}>
          {step === 1 && <WizardStep1Template form={form} setForm={setForm} />}
          {step === 2 && <WizardStep2Details form={form} setForm={setForm} errors={errors} />}
          {step === 3 && <WizardStep3Parties form={form} setForm={setForm} />}
          {step === 4 && (
            <WizardStep4Review
              form={form}
              busy={busy}
              progress={progress}
              onSubmit={() => doSubmit(false)}
              onSubmitDraft={() => doSubmit(true)}
            />
          )}

          {step < 4 && (
            <div className="flex items-center justify-between mt-6 pt-4" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
              <button
                type="button"
                onClick={() => (step === 1 ? navigate("/incidents") : goBack())}
                className="flex items-center gap-1 px-3 py-2 rounded transition-colors"
                style={{
                  background: "transparent", border: "1px solid rgba(255,255,255,0.10)",
                  color: "#c5cad8", fontFamily: "'DM Mono', monospace", fontSize: 11,
                  letterSpacing: "1px", textTransform: "uppercase", cursor: "pointer",
                }}
                data-testid="wizard-back-btn"
              >
                <ArrowLeft size={13} />
                {step === 1 ? "Cancel" : "Back"}
              </button>
              <button
                type="button"
                onClick={goNext}
                disabled={(step === 1 && !canNextStep1) || (step === 2 && !canNextStep2)}
                className="flex items-center gap-1 px-4 py-2 rounded transition-all"
                style={{
                  background:
                    (step === 1 && !canNextStep1) || (step === 2 && !canNextStep2) ? "#0c4076" : "#185FA5",
                  border: "1px solid #185FA5", color: "#fff",
                  fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 13,
                  letterSpacing: "0.3px",
                  cursor:
                    (step === 1 && !canNextStep1) || (step === 2 && !canNextStep2) ? "not-allowed" : "pointer",
                  opacity:
                    (step === 1 && !canNextStep1) || (step === 2 && !canNextStep2) ? 0.6 : 1,
                }}
                data-testid="wizard-next-btn"
              >
                Next: {STEPS[step].label}
                <ChevronRight size={14} />
              </button>
            </div>
          )}

          {step === 4 && (
            <div className="flex items-center justify-between mt-6 pt-4" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
              <button
                type="button"
                onClick={goBack}
                className="flex items-center gap-1 px-3 py-2 rounded transition-colors"
                style={{
                  background: "transparent", border: "1px solid rgba(255,255,255,0.10)",
                  color: "#c5cad8", fontFamily: "'DM Mono', monospace", fontSize: 11,
                  letterSpacing: "1px", textTransform: "uppercase", cursor: "pointer",
                }}
                data-testid="wizard-back-btn"
              >
                <ArrowLeft size={13} /> Back
              </button>
              <span
                style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#6b7388", letterSpacing: "1px", textTransform: "uppercase" }}
              >
                <ArrowRight size={11} className="inline-block mr-1 align-baseline" />
                Final step
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
